import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Paper, ToggleButton, ToggleButtonGroup, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import ReactECharts from 'echarts-for-react';
import { getReviewEntityCountByType, ReviewEntityCountByType } from '../../services/api';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

type ChartType = 'bar' | 'pie';

const ReviewEntitiesByType = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<ReviewEntityCountByType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [chartType, setChartType] = useState<ChartType>('bar');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getReviewEntityCountByType();
            setData(response.data);
        } catch (err: any) {
            console.error('Error fetching review entity count by type:', err);
            setError(err.message || 'Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    const handleChartTypeChange = (
        event: React.MouseEvent<HTMLElement>,
        newChartType: ChartType | null,
    ) => {
        if (newChartType !== null) {
            setChartType(newChartType);
        }
    };

    const getBarChartOption = () => {
        const categories = data.map(item => item.review_entity_type_name);
        const counts = data.map(item => item.review_entity_count);

        return {
            title: {
                text: 'Review Entities by Type',
                left: 'center',
                textStyle: {
                    fontSize: 20,
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                formatter: (params: any) => {
                    const param = params[0];
                    return `${param.name}<br/>Count: <strong>${param.value}</strong>`;
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: categories,
                axisLabel: {
                    interval: 0,
                    rotate: 45,
                    fontSize: 12
                },
                name: 'Review Entity Type',
                nameLocation: 'middle',
                nameGap: 60,
                nameTextStyle: {
                    fontSize: 14,
                    fontWeight: 'bold'
                }
            },
            yAxis: {
                type: 'value',
                name: 'Count',
                nameLocation: 'middle',
                nameGap: 50,
                nameTextStyle: {
                    fontSize: 14,
                    fontWeight: 'bold'
                },
                minInterval: 1
            },
            series: [
                {
                    name: 'Count',
                    type: 'bar',
                    data: counts,
                    itemStyle: {
                        color: '#2563eb',
                        borderRadius: [4, 4, 0, 0]
                    },
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 12,
                        fontWeight: 'bold'
                    },
                    emphasis: {
                        itemStyle: {
                            color: '#1d4ed8'
                        }
                    }
                }
            ]
        };
    };

    const getPieChartOption = () => {
        const pieData = data.map(item => ({
            name: item.review_entity_type_name,
            value: item.review_entity_count
        }));

        return {
            title: {
                text: 'Review Entities by Type',
                left: 'center',
                textStyle: {
                    fontSize: 20,
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => {
                    return `${params.name}<br/>Count: <strong>${params.value}</strong> (${params.percent}%)`;
                }
            },
            legend: {
                orient: 'vertical',
                left: 'left',
                top: 'middle',
                textStyle: {
                    fontSize: 12
                }
            },
            // Explicitly set xAxis and yAxis to empty arrays for pie chart
            xAxis: [],
            yAxis: [],
            grid: {
                show: false
            },
            series: [
                {
                    name: 'Review Entity Type',
                    type: 'pie',
                    radius: ['40%', '70%'],
                    center: ['60%', '50%'],
                    avoidLabelOverlap: true,
                    itemStyle: {
                        borderRadius: 8,
                        borderColor: '#fff',
                        borderWidth: 2
                    },
                    label: {
                        show: true,
                        formatter: '{b}: {c} ({d}%)',
                        fontSize: 12
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 14,
                            fontWeight: 'bold'
                        },
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    },
                    data: pieData
                }
            ]
        };
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <TitleBar />
            <Container maxWidth="xl" sx={{ pt: 6, pb: 8 }}>
                {/* Back Button */}
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/reports')}
                        variant="outlined"
                        sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                    >
                        Back to Reports
                    </Button>
                </Box>

                {/* Report Container - Bounded Box */}
                <Paper 
                    elevation={3}
                    sx={{ 
                        p: 4,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'grey.200'
                    }}
                >
                    {/* Header Section */}
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography 
                                variant="h4" 
                                sx={{ 
                                    fontWeight: 700,
                                    color: 'text.primary',
                                    mb: 1
                                }}
                            >
                                Review Entities by Type
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: 'text.secondary'
                                }}
                            >
                                Distribution of review entities across different types
                            </Typography>
                        </Box>

                        <ToggleButtonGroup
                            value={chartType}
                            exclusive
                            onChange={handleChartTypeChange}
                            aria-label="chart type"
                            size="small"
                        >
                            <ToggleButton value="bar" aria-label="bar chart">
                                <BarChartIcon sx={{ mr: 1 }} />
                                Bar Chart
                            </ToggleButton>
                            <ToggleButton value="pie" aria-label="pie chart">
                                <PieChartIcon sx={{ mr: 1 }} />
                                Pie Chart
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Chart Content */}
                    {loading ? (
                        <Box sx={{ p: 6, textAlign: 'center' }}>
                            <CircularProgress />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Loading report data...
                            </Typography>
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    ) : data.length === 0 ? (
                        <Box sx={{ p: 6, textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary">
                                No data available
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ 
                            bgcolor: '#fafafa', 
                            borderRadius: 2, 
                            p: 2,
                            border: '1px solid',
                            borderColor: 'grey.100'
                        }}>
                            <ReactECharts
                                option={chartType === 'bar' ? getBarChartOption() : getPieChartOption()}
                                style={{ height: '500px', width: '100%' }}
                                opts={{ renderer: 'svg' }}
                                notMerge={true}
                            />
                        </Box>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default ReviewEntitiesByType;
